import { useMutation } from '@tanstack/react-query';
import {
  signUploadSchema,
  UPLOAD_CONTENT_TYPES,
  UPLOAD_MAX_BYTES,
  type SignUploadDto,
  type UploadContentType,
  type UploadKind,
} from '@zoxo/shared';

import { api } from '../../../lib/api';

/**
 * Image upload (AC-1). Two steps, both client-driven:
 *   1. POST /uploads/sign — the API validates contentType (allowlist) + size and
 *      returns a short-lived presigned PUT URL pinned to that contentType.
 *   2. PUT the raw bytes straight to R2 at `uploadUrl` (no Bearer — the signature
 *      authorizes it; sending Authorization would break the signed request). The
 *      Content-Type header MUST match what was signed.
 * On success the stable `publicUrl` is returned for embedding in the tiptap body.
 *
 * Plain mutation (not Suspense) per the frontend rule for file uploads. Type/size
 * are checked client-side first so violations are surfaced before any request.
 */
export class UploadValidationError extends Error {}

function assertUploadable(file: File): asserts file is File {
  if (!(UPLOAD_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    throw new UploadValidationError(
      'PNG·JPG·WEBP·GIF 이미지만 올릴 수 있어요.',
    );
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    throw new UploadValidationError('이미지는 5MB 이하만 올릴 수 있어요.');
  }
}

export function useImageUpload(kind?: UploadKind) {
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      assertUploadable(file);

      const contentType = file.type as UploadContentType;
      // Validate against the shared contract before hitting the API (defense-in-depth).
      // `kind` namespaces the R2 key server-side (e.g. 'article' → articles/...).
      const signBody = signUploadSchema.parse({ contentType, size: file.size, kind });

      const { uploadUrl, publicUrl } = await api.post<SignUploadDto>(
        '/uploads/sign',
        signBody,
      );

      // Direct-to-R2 PUT. No Authorization header — the presigned URL is the auth.
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!put.ok) {
        throw new Error(`이미지 업로드에 실패했어요 (${put.status}).`);
      }

      return publicUrl;
    },
  });
}
